import { AdminApiError, adminFetch } from '../admin/api';
import type { SkinManifest, SkinTokens } from './types';

export interface AdminSkinVersion {
  id: string;
  version: number;
  status: 'draft' | 'published';
  releaseNotes: string;
  publishedAt: string | null;
  updatedAt: string;
}

export interface AdminSkin {
  id: string;
  slug: string;
  name: string;
  description: string;
  sortOrder: number;
  listed: boolean;
  isDefault: boolean;
  latestVersion: number;
  latestStatus: 'draft' | 'published';
  publishedVersion: number | null;
  previewUrl: string | null;
  versions: AdminSkinVersion[];
}

export interface AdminSkinManifest extends SkinManifest {
  editable: boolean;
}

function apiBase(): string {
  const configured = import.meta.env.VITE_API_BASE_URL as string | undefined;
  return (configured?.trim() === '' ? '/api' : configured?.trim() ?? '/api').replace(/\/+$/, '');
}

export function adminAssetUrl(url: string): string {
  if (url.startsWith('/api/')) return `${apiBase()}${url.slice('/api'.length)}`;
  return url;
}

export async function listAdminSkins(): Promise<AdminSkin[]> {
  return (await adminFetch<{ skins: AdminSkin[] }>('/admin/appearance/skins')).skins;
}

export async function createAdminSkin(input: {
  name: string;
  slug: string;
  description: string;
  sortOrder: number;
}): Promise<{ id: string; version: number }> {
  return adminFetch('/admin/appearance/skins', { method: 'POST', body: input });
}

export async function patchAdminSkin(id: string, patch: Record<string, unknown>): Promise<void> {
  await adminFetch(`/admin/appearance/skins/${encodeURIComponent(id)}`, { method: 'PATCH', body: patch });
}

export async function deleteAdminSkin(id: string): Promise<void> {
  await adminFetch(`/admin/appearance/skins/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

export async function createAdminDraft(id: string): Promise<{ version: number; reused: boolean }> {
  return adminFetch(`/admin/appearance/skins/${encodeURIComponent(id)}/versions`, { method: 'POST', body: {} });
}

export async function getAdminVersion(id: string, version: number): Promise<AdminSkinManifest> {
  return adminFetch(`/admin/appearance/skins/${encodeURIComponent(id)}/versions/${String(version)}`);
}

export async function saveAdminDraft(
  id: string,
  version: number,
  tokens: SkinTokens,
  releaseNotes: string,
): Promise<void> {
  await adminFetch(`/admin/appearance/skins/${encodeURIComponent(id)}/versions/${String(version)}`, {
    method: 'PUT',
    body: { tokens, releaseNotes },
  });
}

export async function uploadAdminAsset(
  skinId: string,
  version: number,
  kind: 'wallpaper' | 'preview' | 'font' | 'icon' | 'bubble',
  slot: string,
  file: File,
): Promise<{ id: string; sha256: string; size: number; url: string }> {
  const response = await fetch(
    `${apiBase()}/v1/admin/appearance/skins/${encodeURIComponent(skinId)}/versions/${String(version)}/assets/${kind}/${encodeURIComponent(slot)}`,
    {
      method: 'PUT',
      credentials: 'include',
      cache: 'no-store',
      headers: {
        'Content-Type': file.type,
        'X-File-Name': encodeURIComponent(file.name),
      },
      body: file,
    },
  );
  const payload = await response.json().catch(() => null) as { error?: string } | null;
  if (!response.ok) throw new AdminApiError(response.status, payload?.error);
  return payload as unknown as { id: string; sha256: string; size: number; url: string };
}

export async function deleteAdminAsset(
  skinId: string,
  version: number,
  kind: string,
  slot: string,
): Promise<void> {
  await adminFetch(
    `/admin/appearance/skins/${encodeURIComponent(skinId)}/versions/${String(version)}/assets/${encodeURIComponent(kind)}/${encodeURIComponent(slot)}`,
    { method: 'DELETE' },
  );
}

export interface BatchResult {
  results: { skinId: string; ok: boolean; code?: string; version?: number }[];
}

export async function batchPublish(
  skinIds: string[],
  action: 'publish' | 'unpublish',
  releaseNotes = '',
): Promise<BatchResult> {
  return adminFetch('/admin/appearance/publish-batch', {
    method: 'POST',
    body: { skinIds, action, releaseNotes },
  });
}

export async function setDefaultSkin(skinId: string): Promise<void> {
  await adminFetch('/admin/appearance/default', { method: 'POST', body: { skinId } });
}

